import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  AccountTree as FlowIcon,
  ArrowForward as ArrowIcon,
  Assignment as TaskIcon,
  Extension as ModuleIcon,
  Autorenew as ProcessIcon,
  Code as CodeIcon,
  PeopleAlt as UserIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import DocumentTypeTemplate from '../DocumentTypeTemplate';
import SplitViewProcessFlows from '../SplitViewProcessFlows';
import MermaidTest from '../MermaidTest';

const ProcessFlows = ({ projectId, existingContent = '', onSave }) => {
  const renderProcessFlowsPreview = (content) => {
    // Try to parse as JSON first
    try {
      const flowData = JSON.parse(content);
      return renderStructuredFlow(flowData);
    } catch (e) {
      // If not JSON, render as markdown
      return (
        <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
          <ReactMarkdown>{content}</ReactMarkdown>
        </Box>
      );
    }
  };
  
  // Render a structured flow visualization
  const renderStructuredFlow = (flowData) => {
    return (
      <Box>
        {flowData.flows && flowData.flows.length > 0 ? (
          <Grid container spacing={3}>
            {flowData.flows.map((flow, index) => (
              <Grid item xs={12} key={index}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {flow.name || `Flow ${index + 1}`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {flow.description || 'Process flow description'}
                    </Typography>
                    
                    {flow.steps && flow.steps.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Process Steps
                        </Typography>
                        {flow.steps.map((step, stepIndex) => (
                          <Box 
                            key={stepIndex} 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'flex-start', 
                              mb: 2,
                              ml: 2
                            }}
                          >
                            <Box
                              sx={{
                                bgcolor: 'primary.main',
                                color: 'white',
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mr: 2,
                                fontWeight: 'bold',
                                flexShrink: 0
                              }}
                            >
                              {stepIndex + 1}
                            </Box>
                            <Box>
                              <Typography variant="subtitle2">
                                {step.name || `Step ${stepIndex + 1}`}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {step.description || 'Step description'}
                              </Typography>
                              
                              {stepIndex < flow.steps.length - 1 && (
                                <Box 
                                  sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    color: 'text.disabled',
                                    my: 1,
                                    ml: -1
                                  }}
                                >
                                  <ArrowIcon fontSize="small" />
                                </Box>
                              )}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography variant="body1" color="text.secondary" align="center">
            No process flows defined
          </Typography>
        )}
      </Box>
    );
  };
  
  // Process flows prompt configuration
  const processFlowsPromptConfig = {
    system: "You are a business analyst and systems architect specializing in process flows, user journeys, and system interactions. Create Mermaid diagrams to visualize the process flows.",
    examples: [
      "Create a user registration flow with Mermaid diagram for our e-commerce application",
      "Design a checkout process with payment integration as a Mermaid flowchart"
    ],
    instruction: "Design detailed process flows using Mermaid diagram syntax, including steps, decision points, and system interactions. Make sure to use the ```mermaid syntax for each diagram."
  };

  return (
    <DocumentTypeTemplate
      projectId={projectId}
      documentType="flow"
      title="Process Flows"
      icon={FlowIcon}
      existingContent={existingContent}
      onSave={onSave}
      renderPreview={renderProcessFlowsPreview}
      promptConfig={processFlowsPromptConfig}
      allowedImportTypes="application/json,text/plain,text/markdown"
      chatWrapper={SplitViewProcessFlows}
    >
      {/* Additional process flows content */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Process Flow Templates</Typography>
        <Divider sx={{ mb: 2 }} />
        
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <UserIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle1">User Registration Flow</Typography>
                </Box>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <TaskIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="User enters email and password" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <TaskIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="System validates input" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <TaskIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="System sends verification email" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <TaskIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="User verifies email" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <TaskIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Account activated" />
                  </ListItem>
                </List>
                <Button 
                  variant="outlined" 
                  fullWidth 
                  size="small" 
                  sx={{ mt: 2 }}
                >
                  Use Template
                </Button>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <ProcessIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle1">Order Processing Flow</Typography>
                </Box>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <TaskIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="User adds items to cart" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <TaskIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="User proceeds to checkout" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <TaskIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="System validates order" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <TaskIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Payment processing" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <TaskIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Order confirmation" />
                  </ListItem>
                </List>
                <Button 
                  variant="outlined" 
                  fullWidth 
                  size="small" 
                  sx={{ mt: 2 }}
                >
                  Use Template
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
      
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Process Flow Design Tips</Typography>
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>
              Design Principles
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <ModuleIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Keep it simple"
                  secondary="Focus on the core flow first, then add edge cases"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <ModuleIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Consider error states"
                  secondary="Include error handling and recovery paths"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <ModuleIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Include decision points"
                  secondary="Document all conditionals and branching logic"
                />
              </ListItem>
            </List>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>
              Implementation Notes
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <CodeIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="State management"
                  secondary="Define how state transitions through the flow"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CodeIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Validation rules"
                  secondary="Document all data validation requirements"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CodeIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Integration points"
                  secondary="Define external system interactions"
                />
              </ListItem>
            </List>
          </Grid>
        </Grid>
      </Paper>

      {/* Mermaid Test Section */}
      <Accordion sx={{ mt: 3 }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="mermaid-test-content"
          id="mermaid-test-header"
        >
          <Typography variant="subtitle1" color="primary">
            Mermaid Diagram Test (Click to expand)
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            <Typography variant="body2" color="text.secondary" paragraph>
              This test section helps verify that the Mermaid library is working correctly.
              If you can see a properly rendered flowchart below, then Mermaid is working
              and should display your process flow diagrams correctly.
            </Typography>
            <MermaidTest />
          </Box>
        </AccordionDetails>
      </Accordion>
    </DocumentTypeTemplate>
  );
};

export default ProcessFlows;